import type { WeightedValue } from '@repo/contracts';

/** A `() => number` in `[0, 1)`, drawn from a `Rng`'s own stream. Threaded
 *  explicitly rather than read off a module-level generator, so two calls
 *  with the same seed always produce the same population — no shared,
 *  mutable global RNG to leak state between an API request and a client
 *  re-sample. */
export type RandomSource = () => number;

/**
 * mulberry32 — a small, fast, seeded PRNG.
 *
 * Not cryptographic, and doesn't need to be: this seeds a *population*, not a
 * secret. What matters is that the same seed always produces the same
 * sequence, on the server and in the browser alike, so a client's optimistic
 * re-sample after a weight change lands on exactly what the server would
 * compute — see `LookalikeSpec.sampleSeed`'s own doc.
 */
export function mulberry32(seed: number): RandomSource {
  let a = seed | 0;
  return function random() {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** A standard-normal draw via the Box–Muller transform, from the same
 *  seeded stream — used to jitter a lookalike off the exact midpoint of a
 *  continuous range instead of leaving every draw uniform. */
export function gaussian(random: RandomSource): number {
  const u = 1 - random();
  const v = random();
  return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
}

/** Uniform draw in `[min, max]`. */
export function uniform(
  random: RandomSource,
  min: number,
  max: number,
): number {
  return min + random() * (max - min);
}

/**
 * Picks one value from a weighted pool.
 *
 * Weights need not sum to 1 or to 100 — normalised here by the pool's own
 * total, which is what lets `UpdateLookalikeWeightsDto` change a single
 * value's weight without the caller renormalising the rest of the pool by
 * hand.
 */
export function weightedPick<T>(
  random: RandomSource,
  pool: readonly WeightedValue<T>[],
): T {
  const first = pool[0];
  if (!first) throw new Error('Cannot sample from an empty pool');

  const total = pool.reduce((sum, entry) => sum + Math.max(0, entry.weight), 0);
  if (total <= 0) return first.value;

  const draw = random() * total;
  let cursor = 0;
  for (const entry of pool) {
    cursor += Math.max(0, entry.weight);
    if (draw <= cursor) return entry.value;
  }
  // Floating-point rounding can leave `draw` a hair past the last boundary.
  return pool[pool.length - 1]!.value;
}
